/**
 * People at your home: someone you've invited over (a friend, your partner, family who live elsewhere), and the family
 * you live with, in the evenings. An invitation is for now or later; they come to the door about half an hour after,
 * if you're in, and stay a few hours (never past 23:00). What you do together (talk, watch TV, play the console, eat
 * together) is time spent and brings you closer, like a hangout, without the bill.
 */
import { advance, dayOf, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';
import { isFamily, isPartner } from './lifecycle.ts';
import { abroad } from './travel.ts';
import { Rng } from './rng.ts';

export interface Guest {
  name: string;
  /** when they get to your door (game minutes) */
  at: number;
  /** when they head home */
  until: number;
  /** they've come in */
  in?: boolean;
  /** your partner, staying the night (till the morning) */
  over?: boolean;
  /** the morning after: said */
  morning?: boolean;
}
export type HomeTime = 'chat' | 'tv' | 'game' | 'eat';

const LATE = 23 * 60;

/** Ask someone over: they say yes or no (the closer you are, the likelier; family and your partner always come). */
export function inviteOver(s: LifeState, name: string, at = s.minutes + 30): { ok: boolean; line: string } {
  const c = s.contacts.find((x) => x.name === name), first = name.split(' ')[0];
  if (!c) return { ok: false, line: '' };
  if (abroad(s)) return { ok: false, line: "You're away: there's nowhere of yours here to have them over." };
  if (s.guest && s.guest.until > s.minutes) return { ok: false, line: `${s.guest.name.split(' ')[0]} is coming over already.` };
  const day = Math.floor(at / 1440), latest = day * 1440 + LATE - 90;
  if (at > latest) return { ok: false, line: `"It's too late tonight," says ${first}. "Another day?"` };
  const r = new Rng((s.seed ^ Math.imul(s.minutes + name.length * 97, 2654435761)) >>> 0);
  const sure = isFamily(c.relation) || isPartner(c.relation);
  if (!sure && !r.chance(Math.max(0.15, Math.min(0.95, (c.closeness + 20) / 90)))) return { ok: false, line: `${first} can't make it: "Next time, I promise."` };
  s.guest = { name, at, until: Math.min(at + 240, day * 1440 + LATE) };
  return { ok: true, line: at - s.minutes <= 45 ? `${first} is on the way: about half an hour. Be home to let them in.` : `${first} will come over then. Be home to let them in.` };
}

/** the guest, if they're due at your door now (or already in) */
export function guestNow(s: LifeState): Guest | null {
  const g = s.guest;
  if (!g) return null;
  if (s.minutes >= g.until) { s.guest = null; return null; }
  return s.minutes >= g.at ? g : null;
}

/** You weren't in when they came (an hour at the door): they went home, a little put out. */
export function guestMissed(s: LifeState): string | null {
  const g = s.guest;
  if (!g || g.in || s.minutes < g.at + 60) return null;
  s.guest = null;
  const c = s.contacts.find((x) => x.name === g.name);
  if (c) c.closeness = Math.max(0, c.closeness - 3);
  return `${g.name.split(' ')[0]} came round, but you weren't in. They went home.`;
}

/** The family you live with (a family house): who's in, in the evening (17:00 to 23:00): up to two of them. */
export function familyHome(s: LifeState): string[] {
  if (abroad(s) || !s.card.home.id.startsWith('family')) return [];
  const h = (s.minutes % 1440) / 60;
  if (h < 17 || h >= 23) return [];
  return s.contacts.filter((c) => ['Mother', 'Father', 'Brother', 'Sister'].includes(c.relation)).slice(0, 2).map((c) => c.name);
}

/** Time together at home: closer, less lonely, a good evening. Eating together costs the ingredients. */
export function timeTogether(s: LifeState, name: string, what: HomeTime): { ok: boolean; line: string; events: LifeEvent[] } {
  const c = s.contacts.find((x) => x.name === name), first = name.split(' ')[0];
  if (!c) return { ok: false, line: '', events: [] };
  const r = new Rng((s.seed ^ Math.imul(s.minutes + name.length * 31, 2246822519)) >>> 0);
  if (what === 'eat' && !spend(s, localPrice(s, 3500, 500), `Dinner at home with ${first}`)) return { ok: false, line: "You can't afford to feed two tonight.", events: [] };
  const seenToday = c.seen !== undefined && dayOf(c.seen) === dayOf(s.minutes);
  const gain = (r.range(3, 7) + s.skills.social / 25) * (1 - c.closeness / 130) * (seenToday ? 0.6 : 1) * (what === 'eat' ? 1.4 : 1);
  c.closeness = Math.max(0, Math.min(100, c.closeness + gain));
  c.seen = s.minutes;
  s.needs.social = Math.min(100, s.needs.social + (what === 'chat' ? 18 : 12));
  s.needs.fun = Math.min(100, s.needs.fun + (what === 'chat' ? 6 : what === 'eat' ? 8 : 18));
  if (what === 'eat') s.needs.hunger = Math.min(100, s.needs.hunger + 40);
  if (s.guest?.name === name) s.guest.in = true;
  const fam = isFamily(c.relation), partner = isPartner(c.relation);
  const lines: Record<HomeTime, string[]> = {
    chat: fam ? [`You and ${first} talk about everyone: who's getting married, who owes who, what Grandma said.`, `${first} asks about work, then about your love life, then about work again.`]
      : partner ? [`You and ${first} lie on the sofa and talk about nothing for an hour.`, `${first} tells you about their day; you tell them about yours. The time goes.`]
      : [`You and ${first} catch up properly: the gist, the jokes, the plans.`, `${first} has gist. A lot of it.`],
    tv: [`You and ${first} watch a Nollywood film. ${first} guesses the twist an hour early.`, `The match is on. You and ${first} shout at the screen together.`, `A reality show. You both pretend you're not hooked.`],
    game: [`FIFA on the console. ${first} wins, then wants a rematch, then wins again.`, `You and ${first} play until your thumbs hurt.`],
    eat: [`You cook, ${first} sets the table, and you eat together: rice, stew, more stew.`, `Dinner at home with ${first}: plates scraped clean.`],
  };
  const mins = what === 'chat' ? 30 : what === 'game' ? 45 : 60;
  return { ok: true, line: r.pick(lines[what]), events: advance(s, mins) };
}

/** Your partner, in the evening (from 20:00): ask them to stay the night. They're there in the morning. */
export function canStayOver(s: LifeState, name: string): boolean {
  const c = s.contacts.find((x) => x.name === name), h = (s.minutes % 1440) / 60;
  return !!c && isPartner(c.relation) && s.guest?.name === name && !s.guest.over && (h >= 18 || h < 4);
}
export function stayOver(s: LifeState, name: string): string {
  const g = s.guest, first = name.split(' ')[0];
  if (!g || g.name !== name) return '';
  const day = Math.floor(s.minutes / 1440);
  g.over = true; g.until = (day + 1) * 1440 + 9 * 60 + 30;
  return `${first} smiles. "I was hoping you'd ask." They're staying the night.`;
}
/** The morning after (from 06:00, once): breakfast together, closer. */
export function morningAfter(s: LifeState): string | null {
  const g = s.guest, h = (s.minutes % 1440) / 60;
  if (!g?.over || g.morning || h < 6 || h >= 12 || s.minutes < g.at + 360) return null;
  g.morning = true;
  const c = s.contacts.find((x) => x.name === g.name);
  if (c) c.closeness = Math.min(100, c.closeness + 4);
  s.needs.social = Math.min(100, s.needs.social + 20);
  return `Morning. ${g.name.split(' ')[0]} is up before you: tea, bread and eggs on the table. They head home after breakfast.`;
}

/** They're heading home now (you said goodnight, or it's late). */
export function guestGoes(s: LifeState): string | null {
  const g = s.guest;
  if (!g) return null;
  s.guest = null;
  return `${g.name.split(' ')[0]} heads home. "That was nice. Let's do it again."`;
}
